<script setup lang="ts">
import RlEditableDetail from '@/components/common/RlEditableDetail.vue'
import { useMinerDetails } from '@/composables/rc/miner-details'
import type { IModel } from '@/types/rc/miners'
import { IndividualPart } from '@/types/rc/miners/PartType'
import { useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref, toRefs } from 'vue'
import { DEFAULT_MINER_SN_THRESHOLD } from '@/config/rcapp.config'

const props = defineProps<{
  minerId: number
  prop: string
  partType: IndividualPart
  val?: string | IModel | null
  required?: boolean
  disabled?: boolean
  options?: OptionType[]
  optionsValueKey?: any
  style?: string
}>()

const loading = ref<boolean>()

const { minerId } = toRefs(props)

const queryClient = useQueryClient()
const { minerDetailsFieldMutation } = useMinerDetails(minerId)
const { mutateAsync: mutateMinerDetailsField } = minerDetailsFieldMutation

/**
 * Handles RlEditableField "edit-saved" event.
 *
 * @param field field to update.
 * @param value value of field.
 */
const handleEditSaved = async (value: string | IModel | null) => {
  try {
    loading.value = true
    await mutateMinerDetailsField({ partType: props.partType, field: props.prop, value })
    await queryClient.invalidateQueries({ queryKey: ['miner-details', props.minerId] })
  } catch {
    ElMessage.error('Failed to update miner detail field.')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <rl-editable-detail
    :val="val ?? undefined"
    :options="options"
    options-filterable
    :options-value-key="optionsValueKey"
    :required="required"
    :loading="loading"
    :disabled="disabled"
    :mask="prop === 'mac_address' ? '##:##:##:##:##:##' : undefined"
    :mask-tokens="prop === 'mac_address' ? '#:[0-9a-fA-F]' : undefined"
    :length-threshold="prop === 'sn' ? DEFAULT_MINER_SN_THRESHOLD : undefined"
    :style="style ?? undefined"
    @edit-saved="handleEditSaved"
  />
</template>
